import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button, Card, Table, Td, Th } from '@/components/admin/ui';
import {
  BarList,
  ChartCard,
  DeviceLink,
  EmptyState,
  KpiTile,
  RangePicker,
  UserLink,
} from '@/components';
import type {
  Range,
  StreakLeaderboardEntry,
  StreaksDistributionResponse,
  StreaksLeaderboardResponse,
  StreaksSummaryResponse,
  StreaksTimeseriesResponse,
} from '@/lib/admin-types';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

const STREAKS_HINTS = {
  kpiQualified:
    'Profiles whose last_qualified_local_date equals today in that profile’s timezone.',
  kpiActive:
    'Profiles with current_streak > 0 as stored (streaks reconcile lazily on activity, so this can include recently broken streaks until next open).',
  kpiAtRisk:
    'Active streaks of 3+ days that have not qualified yet today (same threshold as the evening at-risk push).',
  kpiAvg:
    'Mean current_streak among profiles with current_streak > 0.',
  chartRecency:
    'Count of profiles whose most recent qualification date falls on each calendar day. This is current-state recency, not a historical “qualified that day” event log.',
  distCurrent: 'Active profiles bucketed by current streak length.',
  distLongest: 'Profiles bucketed by longest streak ever recorded.',
  distSubject: 'Account-linked streak profiles vs anonymous installation profiles.',
  leaderboardCurrent: 'Top profiles by current streak (then longest, then recently updated).',
  leaderboardLongest: 'Top profiles by longest streak (then current, then recently updated).',
} as const;

type StreaksSearch = { range: Range };

function fmtDay(iso: string) {
  const [, m, day] = iso.split('-');
  return `${m}/${day}`;
}

function chartTooltipFormatter(value: unknown, name: unknown) {
  const v = typeof value === 'number' ? formatInt(value) : String(value);
  return [v, String(name)] as [string, string];
}

function subjectLabel(entry: StreakLeaderboardEntry) {
  if (entry.subject_type === 'account' && entry.account_id) {
    return (
      <UserLink userId={entry.account_id} displayName={entry.display_name}>
        {entry.display_name || entry.account_id.slice(0, 8)}
      </UserLink>
    );
  }
  if (entry.installation_id) {
    return (
      <DeviceLink
        installationId={entry.installation_id}
        nickname={entry.nickname}
      />
    );
  }
  return '—';
}

function LeaderboardTable({
  title,
  tooltip,
  rows,
  loading,
  emptyLabel,
}: {
  title: string;
  tooltip: string;
  rows: StreakLeaderboardEntry[];
  loading: boolean;
  emptyLabel: string;
}) {
  return (
    <Card padding="md" className="cursor-help" title={tooltip}>
      <h2 className="mb-3 text-sm font-semibold text-nova-foreground">{title}</h2>
      {loading && <p className="text-sm text-nova-muted-foreground">Loading…</p>}
      {!loading && rows.length === 0 && <EmptyState title={emptyLabel} />}
      {!loading && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th className="w-12">#</Th>
              <Th>Who</Th>
              <Th className="text-right">Current</Th>
              <Th className="text-right">Longest</Th>
              <Th className="text-right">Freezes</Th>
              <Th>Last qualified</Th>
              <Th>Today</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.subject_type}-${row.account_id ?? row.installation_id}-${row.rank}`}>
                <Td className="tabular-nums text-nova-muted-foreground">{row.rank}</Td>
                <Td>
                  <div className="flex flex-col gap-0.5">
                    {subjectLabel(row)}
                    <span className="text-xs text-nova-muted-foreground">
                      {row.subject_type} · {row.timezone}
                    </span>
                  </div>
                </Td>
                <Td className="text-right tabular-nums font-medium">
                  {formatInt(row.current_streak)}
                </Td>
                <Td className="text-right tabular-nums">
                  {formatInt(row.longest_streak)}
                </Td>
                <Td className="text-right tabular-nums">
                  {formatInt(row.freezes_remaining)}
                </Td>
                <Td className="font-mono text-xs text-nova-muted-foreground">
                  {row.last_qualified_local_date ?? '—'}
                </Td>
                <Td>
                  <span
                    className={
                      row.qualified_today
                        ? 'text-xs font-medium text-emerald-700'
                        : 'text-xs text-nova-muted-foreground'
                    }
                  >
                    {row.qualified_today ? 'Yes' : 'No'}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function StreaksPage() {
  const { range } = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/streaks' });
  const qc = useQueryClient();

  const summaryQ = useQuery({
    queryKey: ['admin', 'streaks', 'summary'],
    queryFn: () =>
      apiJson<StreaksSummaryResponse>('/v1/admin/analytics/streaks/summary'),
  });

  const distQ = useQuery({
    queryKey: ['admin', 'streaks', 'distribution'],
    queryFn: () =>
      apiJson<StreaksDistributionResponse>(
        '/v1/admin/analytics/streaks/distribution'
      ),
  });

  const boardQ = useQuery({
    queryKey: ['admin', 'streaks', 'leaderboard'],
    queryFn: () =>
      apiJson<StreaksLeaderboardResponse>(
        '/v1/admin/analytics/streaks/leaderboard?limit=20'
      ),
  });

  const tsQ = useQuery({
    queryKey: ['admin', 'streaks', 'timeseries', range],
    queryFn: () =>
      apiJson<StreaksTimeseriesResponse>(
        `/v1/admin/analytics/streaks/timeseries?range=${encodeURIComponent(range)}`
      ),
  });

  const loading = summaryQ.isLoading || distQ.isLoading || boardQ.isLoading;
  const err = summaryQ.error || distQ.error || boardQ.error || tsQ.error;
  const summary = summaryQ.data;
  const dist = distQ.data;
  const board = boardQ.data;

  const chartRows = useMemo(
    () =>
      tsQ.data?.days.map((d) => ({
        ...d,
        label: fmtDay(d.date),
      })) ?? [],
    [tsQ.data]
  );

  const subjectBars =
    dist?.by_subject.map((e) => ({
      key: e.key === 'account' ? 'Account' : e.key === 'installation' ? 'Installation' : e.key,
      count: e.count,
    })) ?? [];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-nova-foreground">Streaks</h1>
          <p className="mt-1 text-sm text-nova-muted-foreground">
            Practice streak snapshot from streak profiles — who qualified today, length
            distribution, and leaderboards.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={range}
            onChange={(r) => {
              void navigate({ search: { range: r } });
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'streaks'] });
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-600">
          {(err as Error).message}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void qc.invalidateQueries({ queryKey: ['admin', 'streaks'] })}
          >
            Retry
          </button>
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Qualified today"
          value={summary?.qualified_today ?? 0}
          loading={loading}
          tooltip={STREAKS_HINTS.kpiQualified}
        />
        <KpiTile
          label="Active streaks"
          value={summary?.active_streaks ?? 0}
          loading={loading}
          tooltip={STREAKS_HINTS.kpiActive}
        />
        <KpiTile
          label="At risk (3+)"
          value={summary?.at_risk ?? 0}
          loading={loading}
          tooltip={STREAKS_HINTS.kpiAtRisk}
        />
        <KpiTile
          label="Avg current streak"
          value={
            summary != null ? summary.avg_current_streak.toFixed(2) : '0.00'
          }
          loading={loading}
          tooltip={STREAKS_HINTS.kpiAvg}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Total profiles"
          value={summary?.total_profiles ?? 0}
          loading={loading}
          tooltip="All streak_profiles rows (account-linked and installation)."
        />
        <KpiTile
          label="Streaks 3+"
          value={summary?.streak_3_plus ?? 0}
          loading={loading}
          tooltip="Profiles with current_streak ≥ 3."
        />
        <KpiTile
          label="Best current / longest"
          value={
            summary
              ? `${formatInt(summary.max_current_streak)} / ${formatInt(summary.max_longest_streak)}`
              : '0 / 0'
          }
          loading={loading}
          tooltip="Highest current streak and highest longest streak across all profiles."
        />
        <KpiTile
          label="Freezes exhausted"
          value={summary?.freezes_exhausted ?? 0}
          loading={loading}
          tooltip="Profiles with freezes_remaining = 0."
        />
      </div>

      <ChartCard
        title="Most recent qualification by day"
        tooltip={STREAKS_HINTS.chartRecency}
        loading={tsQ.isLoading}
        error={tsQ.error ? String(tsQ.error) : undefined}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis
              allowDecimals={false}
              width={44}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatInt(Number(v))}
            />
            <Tooltip formatter={chartTooltipFormatter} />
            <Bar
              dataKey="last_qualified_count"
              name="Profiles"
              fill={PRIMARY_HEX}
              radius={[4, 4, 0, 0]}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card padding="md" className="cursor-help" title={STREAKS_HINTS.distCurrent}>
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Current streak</h2>
          {distQ.isLoading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : (
            <BarList
              data={(dist?.by_current_streak ?? []).filter((e) => e.count > 0)}
              emptyLabel="No active streaks"
            />
          )}
        </Card>
        <Card padding="md" className="cursor-help" title={STREAKS_HINTS.distLongest}>
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">Longest streak</h2>
          {distQ.isLoading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : (
            <BarList
              data={(dist?.by_longest_streak ?? []).filter((e) => e.count > 0)}
              emptyLabel="No streak history"
            />
          )}
        </Card>
        <Card padding="md" className="cursor-help" title={STREAKS_HINTS.distSubject}>
          <h2 className="mb-3 text-sm font-semibold text-nova-foreground">By subject</h2>
          {distQ.isLoading ? (
            <p className="text-sm text-nova-muted-foreground">Loading…</p>
          ) : (
            <BarList
              data={subjectBars.filter((e) => e.count > 0)}
              emptyLabel="No profiles"
            />
          )}
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <LeaderboardTable
          title="Leaderboard — current streak"
          tooltip={STREAKS_HINTS.leaderboardCurrent}
          rows={board?.by_current ?? []}
          loading={boardQ.isLoading}
          emptyLabel="No active streaks yet"
        />
        <LeaderboardTable
          title="Leaderboard — longest streak"
          tooltip={STREAKS_HINTS.leaderboardLongest}
          rows={board?.by_longest ?? []}
          loading={boardQ.isLoading}
          emptyLabel="No streak history yet"
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/streaks')({
  validateSearch: (raw: Record<string, unknown>): StreaksSearch => {
    const r = raw.range;
    const range: Range = r === '30d' || r === '90d' || r === '7d' ? r : '7d';
    return { range };
  },
  component: StreaksPage,
});
